import { __awaiter } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useAemConfig } from '@as-react/common-components';
import { cloneDeep } from '@as-react/common-utils';
import { ButtonState } from '@as/cotopaxi';
import CustomerApi from '../../api/customer/CustomerApi';
import { I18N_NAMESPACES } from '../../constants/i18nConstants';
import { analyticsActions } from '../../redux/analytics/actions/AnalyticsActions.creators';
import { AccountPreferences } from '../../components/account/account-preferences/AccountPreferences';
import { DELAY } from '../../constants/GeneralConstants';
import { PreferenceTypes, } from '@as-react/common-types';
import { populatePreferencesWithSubCategories } from '../../util/AccountUtil';
const AccountPreferencesContainer = ({ items, topText, bottomText, pathPrivacy, pathTerms, title, }) => {
    const { t } = useTranslation(I18N_NAMESPACES.account);
    const { defaultRequestParams, servicesEndpoint } = useAemConfig();
    const dispatch = useDispatch();
    const [preferences, setPreferences] = useState();
    const [initialPreferences, setInitialPreferences] = useState();
    const [multiFasciaPreferences, setMultiFasciaPreferences] = useState();
    const [initialMultiFasciaPreferences, setInitialMultiFasciaPreferences] = useState();
    const [preferenceList, setPreferenceList] = useState();
    const [submitButtonState, setSubmitButtonState] = useState(ButtonState.INTERACTIVE);
    const [errorFeedback, setErrorFeedback] = useState('');
    const isUKMarket = defaultRequestParams.market === 'uk';
    const fetchPreferences = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            if (isUKMarket) {
                const result = yield CustomerApi.getMultiFasciaPreferences(servicesEndpoint, defaultRequestParams);
                result.fascias.sort(({ sortOrder: a }, { sortOrder: b }) => a - b);
                setMultiFasciaPreferences(result);
                setInitialMultiFasciaPreferences(cloneDeep(result));
            }
            else {
                const result = yield CustomerApi.getPreferences(servicesEndpoint, defaultRequestParams);
                const preferencesTmp = result.reduce((acc, cur) => (Object.assign(Object.assign({}, acc), { [cur.code]: cur.isSubscribed })), {});
                setPreferenceList(result);
                setPreferences(preferencesTmp);
                setInitialPreferences(cloneDeep(preferencesTmp));
            }
        }
        catch (_a) { }
    });
    const onUpdatePreferencesSuccess = () => {
        setSubmitButtonState(ButtonState.FINISHED);
        setTimeout(() => setSubmitButtonState(ButtonState.INTERACTIVE), DELAY.DELAY_3000);
    };
    const analytics = (currentPreferences, updatedPreferences) => {
        let subscriptions = {};
        let unsubscriptions = {};
        Object.keys(updatedPreferences).map(updatedKey => {
            Object.keys(currentPreferences).map(currentKey => {
                if (currentKey === updatedKey &&
                    updatedPreferences[updatedKey] !==
                        currentPreferences[currentKey]) {
                    if (currentPreferences[currentKey] === false &&
                        updatedPreferences[updatedKey] === true) {
                        subscriptions = Object.assign(Object.assign({}, subscriptions), { [updatedKey]: updatedPreferences[updatedKey] });
                    }
                    else {
                        unsubscriptions = Object.assign(Object.assign({}, unsubscriptions), { [updatedKey]: updatedPreferences[updatedKey] });
                    }
                }
            });
        });
        const hasSubscriptions = Boolean(Object.keys(subscriptions).length);
        const hasUnsubscriptions = Boolean(Object.keys(unsubscriptions).length);
        if (hasSubscriptions && hasUnsubscriptions) {
            dispatch(analyticsActions.newsletterPreferencesSubscriptionUpdate({ subscriptions, unsubscriptions }));
        }
        else if (hasSubscriptions) {
            dispatch(analyticsActions.newsletterPreferencesSubscribe(subscriptions));
        }
        else if (hasUnsubscriptions) {
            dispatch(analyticsActions.newsletterPreferencesUnsubscribe(unsubscriptions));
        }
        dispatch(analyticsActions.newsletterPreferencesUpdate(updatedPreferences));
    };
    const updatePreferences = () => __awaiter(void 0, void 0, void 0, function* () {
        setSubmitButtonState(ButtonState.LOADING);
        setErrorFeedback('');
        try {
            if (isUKMarket) {
                if (!multiFasciaPreferences)
                    return;
                yield CustomerApi.updateMultiFasciaPreferences(servicesEndpoint, defaultRequestParams, multiFasciaPreferences);
                onUpdatePreferencesSuccess();
                if (initialMultiFasciaPreferences) {
                    analytics(initialMultiFasciaPreferences, multiFasciaPreferences);
                }
                setInitialMultiFasciaPreferences(cloneDeep(multiFasciaPreferences));
            }
            else {
                if (!preferences || !preferenceList)
                    return;
                yield CustomerApi.updatePreferences(servicesEndpoint, defaultRequestParams, populatePreferencesWithSubCategories(preferenceList));
                onUpdatePreferencesSuccess();
                if (initialPreferences) {
                    analytics(initialPreferences, preferences);
                }
                setInitialPreferences(cloneDeep(preferences));
            }
        }
        catch (_a) {
            setSubmitButtonState(ButtonState.INTERACTIVE);
            setErrorFeedback(t('account.fail'));
        }
    });
    const onPreferenceChange = (preference, value, fasciaId) => {
        if (isUKMarket) {
            if (!multiFasciaPreferences)
                return;
            if (preference === PreferenceTypes.ETICKET) {
                multiFasciaPreferences.eticket = value;
            }
            else {
                const fascia = multiFasciaPreferences.fascias.find(item => item.fasciaId === fasciaId);
                if (fascia) {
                    fascia.preferences[preference] = value;
                }
            }
            setMultiFasciaPreferences(Object.assign({}, multiFasciaPreferences));
        }
        else {
            if (!preferenceList || !preferences)
                return;
            const preferenceItem = preferenceList.find(item => item.code === preference);
            preferenceList
                .filter(pref => pref.parentPreferenceFlagCode === (preferenceItem === null || preferenceItem === void 0 ? void 0 : preferenceItem.code))
                .forEach(pref => (preferences[pref.code] = value));
            if (preferenceItem)
                preferenceItem.isSubscribed = value;
            preferences[preference] = value;
            const preferenceSubCats = preferenceList.filter(pref => pref.parentPreferenceFlagCode && pref.parentPreferenceFlagCode === (preferenceItem === null || preferenceItem === void 0 ? void 0 : preferenceItem.parentPreferenceFlagCode));
            const allInOneState = preferenceSubCats.every(pref => preferences[pref.code] === value);
            const atLeastOneSubscribed = preferenceSubCats.some(pref => preferences[pref.code] === true);
            const parentPreferenceItem = preferenceList.find(item => item.code === (preferenceItem === null || preferenceItem === void 0 ? void 0 : preferenceItem.parentPreferenceFlagCode));
            if (allInOneState && parentPreferenceItem)
                preferences[parentPreferenceItem.code] = value;
            if (atLeastOneSubscribed && parentPreferenceItem)
                preferences[parentPreferenceItem.code] = true;
            const preferenceListUpdated = preferenceList.map(item => (Object.assign(Object.assign({}, item), { isSubscribed: preferences[item.code] })));
            setPreferenceList(preferenceListUpdated);
            setPreferences(preferences);
        }
    };
    useEffect(() => {
        fetchPreferences();
    }, []);
    return multiFasciaPreferences || preferences ? (_jsx(AccountPreferences, { isUKMarket: isUKMarket, preferenceItemsConfig: items, preferences: preferences, multiFasciaPreferences: multiFasciaPreferences, onPreferenceChange: onPreferenceChange, updatePreferences: updatePreferences, submitButtonState: submitButtonState, topText: topText, bottomText: bottomText, errorFeedback: errorFeedback, pathPrivacy: pathPrivacy, pathTerms: pathTerms, title: title })) : null;
};
export default AccountPreferencesContainer;
